import React from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { DialogTitle } from "@/components/ui/dialog";

/**
 * @typedef {Object} NavLink
 * @property {string} to
 * @property {string} label
 * @property {React.ComponentType<{className?: string}>} [icon]
 */

/**
 * @typedef {Object} CommandPaletteProps
 * @property {boolean} open
 * @property {(v: boolean) => void} onOpenChange
 * @property {NavLink[]} links - the active role's navigation links (computed by AppShell)
 */

/**
 * Global ⌘K command palette. Mounted once in AppShell; receives the already-computed
 * `links` array so it doesn't need to duplicate the role-resolution logic.
 * The keyboard listener (⌘K / Ctrl+K) lives in AppShell — this component only
 * manages what happens once it's open.
 * @param {CommandPaletteProps} props
 */
export default function CommandPalette({ open, onOpenChange, links = [] }) {
  const navigate = useNavigate();

  const handleSelect = (to) => {
    navigate(to);
    onOpenChange(false);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <DialogTitle className="sr-only">Navigation</DialogTitle>
      <CommandInput placeholder="Navigate to…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Navigation">
          {links.map((l) => {
            const Ic = l.icon;
            return (
              <CommandItem
                key={l.to}
                value={l.label}
                onSelect={() => handleSelect(l.to)}
              >
                {Ic && <Ic aria-hidden="true" />}
                <span>{l.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
